import { useEffect, useRef } from "react";

// Premium energy-orb (§9.5). A soft glowing sphere (radial rose→rosePale→
// transparent) with a bloom halo, thin rings that react to the real mic/playback
// `levels`, a subtle waveform and drifting particles. Colours come from the DS
// (rose / rosePale / gold) with animated transitions between states — no pure
// red, no "year-2000 button" bevel.
//
// Public API is unchanged: <VoiceOrb state size levels showLabel />.

// [r,g,b] per state. `core` drives the sphere, `glow` the halo/rings/bars.
const STATE_STYLE = {
  idle:         { core: [190, 154, 251], glow: [161, 107, 247], energy: 0.30 },
  connecting:   { core: [198, 165, 252], glow: [181, 139, 250], energy: 0.55 },
  listening:    { core: [205, 175, 252], glow: [161, 107, 247], energy: 0.78 },
  speaking:     { core: [190, 214, 254], glow: [75, 141, 252], energy: 1.0 },
  thinking:     { core: [240, 200, 150], glow: [232, 184, 107], energy: 0.5 },
  tool:         { core: [240, 205, 150], glow: [232, 184, 107], energy: 0.62 },
  reconnecting: { core: [240, 200, 150], glow: [232, 184, 107], energy: 0.5 },
  error:        { core: [224, 122, 140], glow: [224, 106, 131], energy: 0.55 },
};

export const ORB_LABELS = {
  idle: "Pronto",
  connecting: "Conectando",
  listening: "Ouvindo",
  speaking: "Falando",
  thinking: "Pensando",
  tool: "Executando",
  reconnecting: "Reconectando",
  error: "Erro",
};

// States that show the rotating "working" arc.
const ARC_STATES = new Set(["connecting", "reconnecting", "tool"]);

const lerp = (a, b, t) => a + (b - a) * t;

export default function VoiceOrb({ state = "idle", size = 240, levels = null, showLabel = true }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(state);
  const levelsRef = useRef(levels);
  const rafRef = useRef(0);

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { levelsRef.current = levels; }, [levels]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const coreR = size * 0.19;
    const barInnerR = size * 0.30;
    const barMaxLen = size * 0.15;
    const haloR = size * 0.5;
    const BARS = 72;

    // Smoothed animation state (lerped toward the active style each frame).
    const cur = { core: [190, 154, 251], glow: [161, 107, 247], energy: 0.3, avg: 0 };

    const particles = Array.from({ length: 16 }).map((_, i) => ({
      a: (i / 16) * Math.PI * 2,
      r: coreR * 1.4 + Math.random() * (size * 0.14),
      speed: 0.0008 + Math.random() * 0.0016,
      size: 0.6 + Math.random() * 1.3,
      phase: Math.random() * Math.PI * 2,
    }));

    let t = 0;

    const render = () => {
      const style = STATE_STYLE[stateRef.current] || STATE_STYLE.idle;
      const lv = levelsRef.current;

      // Smooth transitions between states (colour + energy).
      const k = 0.08;
      for (let i = 0; i < 3; i++) {
        cur.core[i] = lerp(cur.core[i], style.core[i], k);
        cur.glow[i] = lerp(cur.glow[i], style.glow[i], k);
      }
      cur.energy = lerp(cur.energy, style.energy, k);

      // Real signal energy (0..1) drives the sphere breathing + ring spread.
      let avg = 0;
      if (lv && lv.length) {
        let s = 0;
        for (let i = 0; i < lv.length; i++) s += lv[i];
        avg = s / lv.length;
      }
      cur.avg = lerp(cur.avg, avg, 0.25);

      const glow = (a) => `rgba(${cur.glow[0] | 0},${cur.glow[1] | 0},${cur.glow[2] | 0},${a})`;
      const core = (a) => `rgba(${cur.core[0] | 0},${cur.core[1] | 0},${cur.core[2] | 0},${a})`;

      ctx.clearRect(0, 0, size, size);
      ctx.globalCompositeOperation = "lighter";

      // 1. Outer bloom halo.
      const bloom = 0.14 + cur.energy * 0.12 + cur.avg * 0.14;
      const halo = ctx.createRadialGradient(cx, cy, coreR * 0.4, cx, cy, haloR);
      halo.addColorStop(0, glow(bloom));
      halo.addColorStop(0.5, glow(bloom * 0.4));
      halo.addColorStop(1, glow(0));
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(cx, cy, haloR, 0, Math.PI * 2);
      ctx.fill();

      // 2. Thin reactive rings (react to real levels).
      for (let ri = 0; ri < 2; ri++) {
        const base = size * (0.34 + ri * 0.075);
        const rr = base + Math.sin(t * 0.03 + ri * 1.4) * (size * 0.008) + cur.avg * size * 0.05 * (ri + 1);
        ctx.strokeStyle = glow((0.12 + cur.energy * 0.16) * (1 - ri * 0.35));
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, rr, 0, Math.PI * 2);
        ctx.stroke();
      }

      // 3. Radial waveform (soft, rounded, reacts to per-band levels).
      for (let i = 0; i < BARS; i++) {
        const a = (i / BARS) * Math.PI * 2 + t * 0.0016;
        let mag;
        if (lv && lv.length) mag = lv[Math.floor((i / BARS) * lv.length) % lv.length];
        else {
          mag = (Math.sin(t * 0.05 + i * 0.4) * 0.5 + 0.5)
              * (0.4 + Math.sin(t * 0.02 + i * 0.16) * 0.16);
        }
        mag = Math.max(0.05, mag) * (0.35 + cur.energy * 0.65);
        const r1 = barInnerR;
        const r2 = r1 + barMaxLen * mag;
        const g = ctx.createLinearGradient(
          cx + Math.cos(a) * r1, cy + Math.sin(a) * r1,
          cx + Math.cos(a) * r2, cy + Math.sin(a) * r2,
        );
        g.addColorStop(0, glow(0.5));
        g.addColorStop(1, glow(0));
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.6;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2);
        ctx.stroke();
      }

      // 4. Drifting particles (subtle sparkle).
      particles.forEach((p) => {
        p.a += p.speed;
        const flicker = 0.4 + Math.sin(t * 0.05 + p.phase) * 0.5;
        ctx.fillStyle = `rgba(240,230,254,${(0.10 + flicker * 0.28) * (0.5 + cur.energy * 0.5)})`;
        ctx.beginPath();
        ctx.arc(cx + Math.cos(p.a) * p.r, cy + Math.sin(p.a) * p.r, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // 5. Core energy-sphere: bright soft centre → rose → transparent.
      const pulse = 1 + Math.sin(t * 0.045) * 0.04 + cur.avg * 0.16;
      const r = coreR * pulse;
      const sphere = ctx.createRadialGradient(cx, cy - r * 0.12, 0, cx, cy, r * 1.15);
      sphere.addColorStop(0, "rgba(255,255,255,0.95)");
      sphere.addColorStop(0.22, core(0.95));
      sphere.addColorStop(0.6, glow(0.55));
      sphere.addColorStop(1, glow(0));
      ctx.fillStyle = sphere;
      ctx.beginPath();
      ctx.arc(cx, cy, r * 1.15, 0, Math.PI * 2);
      ctx.fill();

      // 6. Working arc for connecting / reconnecting / tool.
      if (ARC_STATES.has(stateRef.current)) {
        ctx.strokeStyle = glow(0.7);
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        const a0 = t * 0.06;
        ctx.beginPath();
        ctx.arc(cx, cy, size * 0.27, a0, a0 + Math.PI * 0.5);
        ctx.stroke();
      }

      ctx.globalCompositeOperation = "source-over";
      t += 1;
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(rafRef.current);
  }, [size]);

  return (
    <div className="flex flex-col items-center gap-3" data-testid="voice-orb" data-orb-state={state}>
      <canvas ref={canvasRef} className="orb-canvas" />
      {showLabel && (
        <div className="font-ui text-[12px] tracking-[0.2em] uppercase text-brand-ink2">
          {ORB_LABELS[state] || "—"}
        </div>
      )}
    </div>
  );
}
